/** Carnet de notes d'une fiche entreprise.
 *
 *  Remplace le champ texte unique par des notes datées et signées : ce qui a
 *  été dit lors d'un appel il y a trois semaines ne doit pas être écrasé par
 *  la note du jour. Chaque note porte une catégorie, des tags, peut être
 *  épinglée en tête de fiche et déclencher une tâche de rappel.
 */
import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/crmApi";
import { useAuth } from "@/context/CrmAuth";
import RichTextEditor, { RichTextView } from "@/crm/components/RichTextEditor";
import ProofreadButton from "@/crm/components/ProofreadButton";
import { toast } from "sonner";
import {
  Pin, PinOff, Pencil, Trash2, Copy, Search, Download, Bell, StickyNote,
  X, Check, ChevronDown, LayoutTemplate, Plus, CheckSquare, Loader2,
} from "lucide-react";

/** Doit rester aligné sur NOTE_CATEGORIES (backend/crm/routers/notes.py). */
export const CATEGORIES = [
  { code: "GENERAL", label: "Général", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  { code: "APPEL", label: "Appel", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { code: "RDV", label: "Rendez-vous", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  { code: "BESOIN", label: "Besoin", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  { code: "OBJECTION", label: "Objection", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  { code: "NEGOCIATION", label: "Négociation", cls: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200" },
  { code: "CONCURRENCE", label: "Concurrence", cls: "bg-violet-100 text-violet-700 border-violet-200" },
  { code: "INTERNE", label: "Interne", cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
];
const CAT = Object.fromEntries(CATEGORIES.map((c) => [c.code, c]));

/** Trames de compte-rendu : écrire une note structurée en deux clics plutôt
 *  que de repartir d'une page blanche à chaque appel. */
const TEMPLATES = [
  {
    label: "Compte-rendu d'appel", category: "APPEL",
    body: "<h3>Compte-rendu d'appel</h3><ul><li>Interlocuteur : </li><li>Contexte : </li>"
      + "<li>Besoin exprimé : </li><li>Objections : </li><li>Prochaine étape : </li></ul>",
  },
  {
    label: "Préparation de RDV", category: "RDV",
    body: "<h3>Préparation du rendez-vous</h3><ul><li>Date / lieu : </li><li>Participants : </li>"
      + "<li>Objectif : </li><li>Points à aborder : </li></ul>",
  },
  {
    label: "Qualification (BANT)", category: "BESOIN",
    body: "<h3>Qualification</h3><ul><li>Budget : </li><li>Décideur : </li>"
      + "<li>Besoin : </li><li>Échéance : </li></ul>",
  },
  {
    label: "Objection rencontrée", category: "OBJECTION",
    body: "<h3>Objection</h3><ul><li>Objection : </li><li>Réponse apportée : </li>"
      + "<li>À retenir : </li></ul>",
  },
  {
    label: "Concurrent en place", category: "CONCURRENCE",
    body: "<h3>Concurrence</h3><ul><li>Concurrent : </li><li>Depuis : </li>"
      + "<li>Points faibles : </li><li>Fin de contrat : </li></ul>",
  },
];

const draftKey = (companyId) => `prospee_note_draft_${companyId}`;

/** Texte brut d'un fragment HTML, sans l'insérer dans le document (aucune
 *  ressource chargée) — sert à réhydrater le compteur d'un brouillon repris. */
function htmlToText(html) {
  if (!html) return "";
  try {
    return new DOMParser().parseFromString(html, "text/html").body.textContent || "";
  } catch { return ""; }
}

/** "il y a 2 h", "hier", "12 mars" — repère plus vite qu'un horodatage brut. */
function timeAgo(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  if (s < 172800) return "hier";
  if (s < 604800) return `il y a ${Math.floor(s / 86400)} jours`;
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function initials(name) {
  return (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
}

function CategoryBadge({ code }) {
  const c = CAT[code] || CAT.GENERAL;
  return <span className={`inline-flex px-2 py-0.5 rounded-md border text-[11px] font-medium ${c.cls}`}>{c.label}</span>;
}

export default function CompanyNotes({ company, onChanged }) {
  const { user } = useAuth();
  const companyId = company.id;
  const [data, setData] = useState({ items: [], total: 0, authors: [], legacy_notes: "" });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [category, setCategory] = useState("");
  const [authorId, setAuthorId] = useState("");
  const [pinnedOnly, setPinnedOnly] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/companies/${companyId}/notes`, {
        params: {
          search: debounced || undefined, category: category || undefined,
          author_id: authorId || undefined, pinned: pinnedOnly ? "true" : undefined,
        },
      });
      setData(res);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Chargement des notes impossible"); }
    finally { setLoading(false); }
  }, [companyId, debounced, category, authorId, pinnedOnly]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { load(); onChanged?.(); };

  const canEdit = (n) => user?.role !== "commercial" || n.user_id === user?.id;
  const filtering = Boolean(debounced || category || authorId || pinnedOnly);

  const exportNotes = () => {
    const lines = data.items.map((n) => {
      const cat = (CAT[n.category] || CAT.GENERAL).label;
      const date = new Date(n.created_at).toLocaleString("fr-FR");
      const tags = n.tags?.length ? ` [${n.tags.join(", ")}]` : "";
      return `## ${cat} — ${n.user_name} — ${date}${n.pinned ? " (épinglée)" : ""}${tags}\n\n${n.content_text || ""}\n`;
    });
    const blob = new Blob([`# Notes — ${company.nom}\n\n${lines.join("\n")}`], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `notes-${(company.nom || "entreprise").replace(/[^\w-]+/g, "_").toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const migrate = async () => {
    try { await api.post(`/companies/${companyId}/notes/migrate`); toast.success("Notes historiques reprises"); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Échec de la reprise"); }
  };

  return (
    <div data-testid="notes-panel">
      <NoteComposer company={company} onSaved={refresh} />

      {/* Ancien champ libre de la fiche : proposé à la reprise, jamais perdu. */}
      {data.legacy_notes && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="legacy-notes">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-semibold text-amber-800 mb-1">Notes historiques de la fiche</div>
              <div className="text-sm text-amber-900 whitespace-pre-wrap break-words">{data.legacy_notes}</div>
            </div>
            <button data-testid="migrate-notes" onClick={migrate}
              className="shrink-0 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs hover:bg-amber-700">
              Convertir en note
            </button>
          </div>
        </div>
      )}

      {/* Barre de recherche / filtres : n'apparaît qu'à partir de 3 notes,
          inutile d'encombrer une fiche qui n'en compte qu'une. */}
      {(data.total >= 3 || filtering) && (
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="notes-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher dans les notes…"
              className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <select data-testid="notes-category-filter" value={category} onChange={(e) => setCategory(e.target.value)}
            className="px-2 py-1.5 rounded-lg border border-slate-300 text-sm bg-white">
            <option value="">Toutes catégories</option>
            {CATEGORIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
          {data.authors.length > 1 && (
            <select value={authorId} onChange={(e) => setAuthorId(e.target.value)}
              className="px-2 py-1.5 rounded-lg border border-slate-300 text-sm bg-white">
              <option value="">Tous les auteurs</option>
              {data.authors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          )}
          <button data-testid="notes-pinned-filter" onClick={() => setPinnedOnly((v) => !v)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-sm ${pinnedOnly ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
            <Pin size={14} /> Épinglées
          </button>
          <button onClick={exportNotes} disabled={!data.items.length} title="Exporter les notes affichées"
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-300 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40">
            <Download size={14} /> Exporter
          </button>
        </div>
      )}

      <div className="mt-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin" /> Chargement des notes…
          </div>
        ) : !data.items.length ? (
          <div className="text-center py-8" data-testid="notes-empty">
            <StickyNote size={28} className="mx-auto text-slate-300" />
            <p className="text-sm text-slate-400 mt-2">
              {filtering ? "Aucune note ne correspond à ces filtres" : "Aucune note — la première ci-dessus"}
            </p>
            {filtering && (
              <button onClick={() => { setSearch(""); setCategory(""); setAuthorId(""); setPinnedOnly(false); }}
                className="mt-2 text-xs text-indigo-600 hover:underline">Réinitialiser les filtres</button>
            )}
          </div>
        ) : (
          <>
            <div className="text-xs text-slate-400">
              {data.items.length} note{data.items.length > 1 ? "s" : ""}
              {filtering && data.total !== data.items.length ? ` sur ${data.total}` : ""}
            </div>
            {data.items.map((n) => (
              <NoteCard key={n.id} note={n} company={company} editable={canEdit(n)} onChanged={refresh} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function NoteComposer({ company, onSaved }) {
  const companyId = company.id;
  const [content, setContent] = useState(() => localStorage.getItem(draftKey(companyId)) || "");
  // Le brouillon repris doit être enregistrable sans retoucher le texte :
  // le compteur et le bouton se basent sur le texte, pas sur le HTML.
  const [text, setText] = useState(() => htmlToText(localStorage.getItem(draftKey(companyId))));
  const [category, setCategory] = useState("GENERAL");
  const [pinned, setPinned] = useState(false);
  const [timeline, setTimeline] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [reminder, setReminder] = useState("");
  const [showTemplates, setShowTemplates] = useState(false);
  const [saving, setSaving] = useState(false);
  const contactOptions = company.contacts || [];
  const [contactId, setContactId] = useState("");

  // Brouillon : un changement d'onglet ou un appel entrant ne doit pas faire
  // perdre ce qui vient d'être tapé.
  useEffect(() => {
    if (content) localStorage.setItem(draftKey(companyId), content);
    else localStorage.removeItem(draftKey(companyId));
  }, [content, companyId]);

  const addTag = (raw) => {
    const t = raw.trim().replace(/^#/, "").toUpperCase().slice(0, 30);
    if (t && !tags.includes(t) && tags.length < 10) setTags([...tags, t]);
    setTagInput("");
  };

  const applyTemplate = (tpl) => {
    setCategory(tpl.category);
    // La trame s'ajoute à la suite : on ne perd pas ce qui a déjà été noté.
    setContent((c) => (c ? `${c}${tpl.body}` : tpl.body));
    setShowTemplates(false);
  };

  const reset = () => {
    setContent(""); setText(""); setCategory("GENERAL"); setPinned(false); setTimeline(false);
    setTags([]); setTagInput(""); setReminder(""); setContactId("");
    localStorage.removeItem(draftKey(companyId));
  };

  const save = async () => {
    if (!text.trim()) return toast.error("La note est vide");
    setSaving(true);
    try {
      await api.post(`/companies/${companyId}/notes`, {
        content, category, tags, pinned,
        contact_id: contactId || null,
        add_to_timeline: timeline,
        reminder_date: reminder || null,
        reminder_title: reminder ? `Suite à la note du ${new Date().toLocaleDateString("fr-FR")}` : null,
      });
      toast.success(reminder ? "Note enregistrée et rappel créé" : "Note enregistrée");
      reset();
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="note-composer">
      <div className="flex flex-wrap gap-1.5 mb-2">
        {CATEGORIES.map((c) => (
          <button key={c.code} data-testid={`note-cat-${c.code}`} onClick={() => setCategory(c.code)}
            className={`px-2 py-0.5 rounded-md border text-[11px] font-medium transition-colors ${category === c.code ? c.cls : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}>
            {c.label}
          </button>
        ))}
      </div>

      <div data-testid="notes-area">
        <RichTextEditor value={content} onSubmit={save} minHeight={110}
          onChange={(html, plain) => { setContent(html); setText(plain); }}
          placeholder="Nouvelle note… (Ctrl+Entrée pour enregistrer)" />
        {/* Correction rapide : proposition du modèle, appliquée seulement sur
            demande. La mise en forme fine (gras, couleurs) est alors simplifiée. */}
        <ProofreadButton className="mt-1.5" text={text}
          onApply={(plain, html) => { setContent(html); setText(plain); }} />
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        {tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 rounded-md px-2 py-0.5 text-[11px]">
            #{t}<button onClick={() => setTags(tags.filter((x) => x !== t))}><X size={10} /></button>
          </span>
        ))}
        <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} data-testid="note-tag-input"
          onKeyDown={(e) => { if ((e.key === "Enter" || e.key === ",") && tagInput.trim()) { e.preventDefault(); addTag(tagInput); } }}
          onBlur={() => tagInput.trim() && addTag(tagInput)}
          placeholder="+ tag" className="w-16 px-2 py-0.5 rounded border border-slate-200 bg-white text-[11px]" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3 pt-2 border-t border-slate-200">
        <div className="relative">
          <button data-testid="note-templates" onClick={() => setShowTemplates((v) => !v)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white text-xs text-slate-600 hover:bg-slate-50">
            <LayoutTemplate size={13} /> Modèle <ChevronDown size={12} />
          </button>
          {showTemplates && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowTemplates(false)} />
              <div className="absolute z-20 mt-1 w-64 rounded-lg border border-slate-200 bg-white shadow-lg py-1">
                {TEMPLATES.map((t) => (
                  <button key={t.label} onClick={() => applyTemplate(t)}
                    className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50">
                    {t.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <button data-testid="note-pin-toggle" onClick={() => setPinned((v) => !v)} title="Épingler en tête de fiche"
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs ${pinned ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}>
          <Pin size={13} /> Épingler
        </button>

        <label className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs cursor-pointer ${timeline ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}
          title="Faire apparaître la note dans la timeline des activités">
          <input type="checkbox" className="hidden" checked={timeline} onChange={(e) => setTimeline(e.target.checked)} />
          <CheckSquare size={13} /> Timeline
        </label>

        <label className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white text-xs text-slate-600"
          title="Créer une tâche de rappel à cette date">
          <Bell size={13} />
          <input type="date" data-testid="note-reminder" value={reminder} onChange={(e) => setReminder(e.target.value)}
            className="bg-transparent outline-none text-xs" />
        </label>

        {contactOptions.length > 0 && (
          <select value={contactId} onChange={(e) => setContactId(e.target.value)}
            className="px-2 py-1.5 rounded-lg border border-slate-300 bg-white text-xs text-slate-600">
            <option value="">Sans contact</option>
            {contactOptions.map((ct) => (
              <option key={ct.id} value={ct.id}>{[ct.prenom, ct.nom].filter(Boolean).join(" ")}</option>
            ))}
          </select>
        )}

        <span className="ml-auto text-[11px] text-slate-400">{text.trim().length} car.</span>
        {content && (
          <button onClick={reset} className="px-2.5 py-1.5 rounded-lg text-xs text-slate-500 hover:bg-slate-100">Effacer</button>
        )}
        <button data-testid="save-notes" onClick={save} disabled={saving || !text.trim()}
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-40">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Enregistrer
        </button>
      </div>
    </div>
  );
}

function NoteCard({ note, company, editable, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.content);
  const [draftText, setDraftText] = useState(note.content_text || "");
  const [category, setCategory] = useState(note.category);
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);

  const patch = async (payload, okMsg) => {
    setBusy(true);
    try { await api.put(`/notes/${note.id}`, payload); if (okMsg) toast.success(okMsg); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Échec"); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!draftText.trim()) return toast.error("La note est vide");
    await patch({ content: draft, category }, "Note modifiée");
    setEditing(false);
  };

  const del = async () => {
    setBusy(true);
    try { await api.delete(`/notes/${note.id}`); toast.success("Note supprimée"); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Échec"); }
    finally { setBusy(false); setConfirmDel(false); }
  };

  // Transformer une note en tâche : « il faut rappeler jeudi » devient une
  // vraie échéance au lieu d'une ligne de texte.
  const toTask = async () => {
    try {
      await api.post("/tasks", {
        company_id: company.id, type: "FOLLOW_UP",
        title: (note.content_text || "Note").split("\n")[0].slice(0, 120),
        due_date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
        priority: "medium",
      });
      toast.success("Tâche créée pour demain");
      onChanged();
    } catch { toast.error("Échec de la création de tâche"); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(note.content_text || ""); toast.success("Note copiée"); }
    catch { toast.error("Copie impossible"); }
  };

  return (
    <div data-testid="note-item"
      className={`rounded-xl border p-3.5 transition-colors ${note.pinned ? "border-indigo-200 bg-indigo-50/40" : "border-slate-200 bg-white hover:border-slate-300"}`}>
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 shrink-0 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-[11px] font-semibold">
          {initials(note.user_name)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            {note.pinned && <Pin size={13} className="text-indigo-600" />}
            <CategoryBadge code={note.category} />
            <span className="text-xs text-slate-500">{note.user_name}</span>
            <span className="text-xs text-slate-400" title={new Date(note.created_at).toLocaleString("fr-FR")}>
              · {timeAgo(note.created_at)}
            </span>
            {note.edited && <span className="text-[11px] text-slate-400 italic">· modifiée</span>}
            {note.task_id && <span className="inline-flex items-center gap-1 text-[11px] text-amber-600"><Bell size={11} /> rappel</span>}
          </div>

          {editing ? (
            <div>
              <RichTextEditor value={draft} onSubmit={saveEdit} minHeight={120} autoFocus
                onChange={(html, plain) => { setDraft(html); setDraftText(plain); }}
                placeholder="Modifier la note…" />
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <select value={category} onChange={(e) => setCategory(e.target.value)}
                  className="px-2 py-1 rounded-lg border border-slate-300 text-xs bg-white">
                  {CATEGORIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
                <button data-testid="note-save-edit" onClick={saveEdit} disabled={busy}
                  className="flex items-center gap-1 px-3 py-1 rounded-lg bg-indigo-600 text-white text-xs hover:bg-indigo-700 disabled:opacity-40">
                  <Check size={13} /> Enregistrer
                </button>
                <button onClick={() => { setEditing(false); setDraft(note.content); setDraftText(note.content_text || ""); setCategory(note.category); }}
                  className="px-3 py-1 rounded-lg text-xs text-slate-500 hover:bg-slate-100">Annuler</button>
                <span className="text-[11px] text-slate-400">Ctrl+Entrée pour enregistrer</span>
              </div>
            </div>
          ) : (
            <>
              <RichTextView html={note.content} />
              {note.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {note.tags.map((t) => (
                    <span key={t} className="bg-slate-100 text-slate-500 rounded px-1.5 py-0.5 text-[11px]">#{t}</span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {!editing && (
          <div className="flex items-center gap-0.5 shrink-0">
            <button onClick={copy} title="Copier" className="p-1.5 rounded-lg text-slate-300 hover:text-slate-600 hover:bg-slate-100"><Copy size={14} /></button>
            {editable && (
              <>
                <button data-testid="note-toggle-pin" onClick={() => patch({ pinned: !note.pinned })} disabled={busy}
                  title={note.pinned ? "Désépingler" : "Épingler"}
                  className={`p-1.5 rounded-lg hover:bg-slate-100 ${note.pinned ? "text-indigo-600" : "text-slate-300 hover:text-slate-600"}`}>
                  {note.pinned ? <PinOff size={14} /> : <Pin size={14} />}
                </button>
                <button onClick={toTask} title="Créer une tâche à partir de la note"
                  className="p-1.5 rounded-lg text-slate-300 hover:text-amber-600 hover:bg-slate-100"><CheckSquare size={14} /></button>
                <button data-testid="note-edit" onClick={() => setEditing(true)} title="Modifier"
                  className="p-1.5 rounded-lg text-slate-300 hover:text-indigo-600 hover:bg-slate-100"><Pencil size={14} /></button>
                <button data-testid="note-delete" onClick={() => setConfirmDel(true)} title="Supprimer"
                  className="p-1.5 rounded-lg text-slate-300 hover:text-rose-500 hover:bg-slate-100"><Trash2 size={14} /></button>
              </>
            )}
          </div>
        )}
      </div>

      {confirmDel && (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2">
          <span className="text-xs text-rose-700">Supprimer cette note définitivement ?</span>
          <div className="flex gap-2">
            <button data-testid="note-delete-confirm" onClick={del} disabled={busy}
              className="px-3 py-1 rounded-lg bg-rose-600 text-white text-xs hover:bg-rose-700 disabled:opacity-40">Supprimer</button>
            <button onClick={() => setConfirmDel(false)} className="px-3 py-1 rounded-lg text-xs text-slate-500 hover:bg-white">Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}
